import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Button, Spinner, Alert, Pagination, Form, Row, Col, Modal } from 'react-bootstrap';
import { FiCloudRain, FiRefreshCw, FiEye, FiEdit, FiTrash2, FiMail } from 'react-icons/fi';
import { fetchTripsForecastsRequest } from '../../store/actions/tripsActions';
import { sendForecastTriggeredBulkEmail, deleteTripForecast } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import usePersistedSearchState from '../../hooks/usePersistedSearchState';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminTripsForecasts.css';

const formatWeatherParameter = (item) => {
    const collect = (value) => {
        if (Array.isArray(value)) return value.filter(Boolean);
        if (value != null && String(value).trim() !== '') return [String(value).trim()];
        return [];
    };

    const fromSubcoverageTrips = (() => {
        const lists = [
            item?.reference_info?.subcoverage_trips,
            item?.subcoverage_trips,
            item?.activity_info?.subcoverage_trips,
        ];
        const params = [];
        lists.forEach((list) => {
            if (!Array.isArray(list)) return;
            list.forEach((sub) => {
                params.push(...collect(sub?.weather_parameter));
            });
        });
        return params;
    })();

    const candidates = [
        ...fromSubcoverageTrips,
        ...collect(item?.forecast_info?.weather_parameter),
        ...collect(item?.weather_parameter),
        ...collect(item?.subcoverage_trip?.weather_parameter),
        ...collect(item?.subcoverage_info?.weather_parameter),
        ...collect(item?.reference_info?.weather_parameter),
        ...collect(item?.activity_info?.weather_parameter),
    ];

    const unique = Array.from(new Set(candidates));
    return unique.length > 0 ? unique.join(', ') : 'N/A';
};

const EMPTY_TRIPS_FORECASTS_FILTERS = {
    policy_code: '',
    activity_start_date: '',
    trip_day: '',
    reservation_number: '',
    client_first_name: '',
    client_last_name: '',
    client_email: '',
};

const AdminTripsForecasts = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { isAuthenticated } = useSelector((state) => state.adminAuth);
    const forecastsState = useSelector((state) => state.trips.forecasts);

    const {
        filters,
        setFilters,
        appliedFilters,
        setAppliedFilters,
        currentPage,
        setCurrentPage,
        itemsPerPage,
        clearSearchState,
    } = usePersistedSearchState('trips-forecasts', EMPTY_TRIPS_FORECASTS_FILTERS, {
        persistWhen: isAuthenticated,
    });
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [emailLoading, setEmailLoading] = useState(false);
    const [emailResult, setEmailResult] = useState(null);
    const [emailError, setEmailError] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    const [deleteSuccess, setDeleteSuccess] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedForecast, setSelectedForecast] = useState(null);

    const handleDetails = (item) => {
        const forecastId = getDeep(item, ['forecast_info', 'forecast_id']);
        if (forecastId) {
            navigate(`/admin/trips/forecasts/${forecastId}`);
        } else {
            console.log('Details trip forecast:', item);
            alert(`Détails forecast pour police ${getDeep(item, ['policy_info', 'policy_code'], '')}`);
        }
    };

    const handleEdit = (item) => {
        console.log('Edit trip forecast:', item);
        alert('Fonction éditer à implémenter');
    };

    const handleDelete = (item) => {
        setSelectedForecast(item);
        setShowDeleteModal(true);
        setDeleteError(null);
    };

    const handleCloseDeleteModal = () => {
        if (deleteLoading) return;
        setShowDeleteModal(false);
        setSelectedForecast(null);
    };

    const confirmDelete = async () => {
        const forecastId = getDeep(selectedForecast, ['forecast_info', 'forecast_id'], null);
        if (!forecastId) {
            setDeleteError('Impossible de trouver l’identifiant de la prévision.');
            return;
        }

        try {
            setDeleteLoading(true);
            setDeleteError(null);
            setDeleteSuccess(null);
            await deleteTripForecast(forecastId);
            setDeleteSuccess('Prévision supprimée avec succès.');
            setShowDeleteModal(false);
            setSelectedForecast(null);
            loadForecasts(currentPage);
        } catch (error) {
            setDeleteError(handleApiError(error, 'Erreur lors de la suppression de la prévision'));
        } finally {
            setDeleteLoading(false);
        }
    };

    // Charger les prévisions avec pagination et filtres
    const loadForecasts = useCallback((page = 1, filtersToUse = null) => {
        const skip = (page - 1) * itemsPerPage;
        const params = {
            skip,
            limit: itemsPerPage,
        };

        // Utiliser les filtres fournis ou les filtres appliqués
        const activeFilters = filtersToUse !== null ? filtersToUse : appliedFilters;

        // Ajouter les filtres non vides
        Object.keys(activeFilters).forEach((key) => {
            if (activeFilters[key] && activeFilters[key].trim() !== '') {
                params[key] = activeFilters[key].trim();
            }
        });

        dispatch(fetchTripsForecastsRequest(params));
    }, [itemsPerPage, appliedFilters, dispatch]);

    useEffect(() => {
        if (isAuthenticated) {
            loadForecasts(currentPage);
        }
        /* eslint-disable-next-line */
    }, [isAuthenticated]);

    useEffect(() => {
        loadForecasts(currentPage);
    }, [currentPage]);

    // Extraire les données et métadonnées de la réponse API
    const forecastsData = forecastsState?.data?.data || forecastsState?.data?.forecast_details || forecastsState?.data?.policies_with_forecasts || forecastsState?.data?.forecasts || forecastsState?.data || [];
    const forecastsArray = Array.isArray(forecastsData) ? forecastsData : [];
    const meta = forecastsState?.data?.meta || {
        total: 0,
        skip: 0,
        limit: itemsPerPage,
        page: currentPage,
        total_pages: 0,
    };

    // Pagination côté serveur - utiliser les métadonnées de l'API
    const totalPages = meta.total_pages || Math.max(1, Math.ceil(meta.total / itemsPerPage));
    const totalItems = meta.total || 0;
    const indexOfFirstItem = meta.skip || 0;
    const indexOfLastItem = Math.min(indexOfFirstItem + forecastsArray.length, totalItems);

    const handleRefresh = () => {
        loadForecasts(currentPage);
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleApplyFilters = () => {
        setAppliedFilters({ ...filters });
        setCurrentPage(1);
        loadForecasts(1, filters);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleApplyFilters();
        }
    };

    const handleClearFilters = () => {
        const emptyFilters = { ...EMPTY_TRIPS_FORECASTS_FILTERS };
        clearSearchState();
        setTimeout(() => {
            loadForecasts(1, emptyFilters);
        }, 100);
    };

    const handleSendEmail = async () => {
        try {
            setEmailLoading(true);
            setEmailError(null);
            setEmailResult(null);
            const result = await sendForecastTriggeredBulkEmail();
            setEmailResult(result);
            // Recharger les prévisions après l'envoi
            loadForecasts(currentPage);
        } catch (error) {
            setEmailError(handleApiError(error, 'Erreur lors de l\'envoi des emails'));
        } finally {
            setEmailLoading(false);
        }
    };

    const handleOpenEmailModal = () => {
        setShowEmailModal(true);
        setEmailError(null);
        setEmailResult(null);
    };

    const handleCloseEmailModal = () => {
        setShowEmailModal(false);
        setEmailError(null);
        setEmailResult(null);
    };

    // Calculate pagination range (show max 5 pages around current)
    const getPaginationRange = () => {
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        return { startPage, endPage };
    };

    // Utiliser directement les prévisions de l'API (pas de filtrage côté client)
    const currentForecasts = forecastsArray;

    const getDeep = (obj, path, fallback = 'N/A') => { try { const val = path.reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), obj); return val ?? fallback; } catch { return fallback; } };

    return (
        <div className="dashboard-page admin-trips-forecasts-page">
            <Card>
                <Card.Body>
                    <div className="page-header">
                        <div>
                            <h2 className="page-title">
                                <FiCloudRain className="me-2" />
                                Trip - Gestion Prévisions
                            </h2>
                            <p className="text-muted mb-0">Prévisions liées aux séjours</p>
                        </div>
                    </div>

                    {forecastsState.error && (
                        <Alert variant="danger" className="mt-3">
                            {forecastsState.error}
                        </Alert>
                    )}
                    {deleteError && (
                        <Alert variant="danger" className="mt-3" dismissible onClose={() => setDeleteError(null)}>
                            {deleteError}
                        </Alert>
                    )}
                    {deleteSuccess && (
                        <Alert variant="success" className="mt-3" dismissible onClose={() => setDeleteSuccess(null)}>
                            {deleteSuccess}
                        </Alert>
                    )}

                    {/* Filtres avancés */}
                    <Card className="mb-3">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-0">Filtres de recherche</h6>
                            <div className="d-flex gap-2">
                                <Button variant="outline-secondary" size="sm" onClick={handleClearFilters}>
                                    Réinitialiser
                                </Button>
                                <Button variant="primary" size="sm" onClick={handleApplyFilters}>
                                    Appliquer les filtres
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Code police</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="policy_code"
                                            value={filters.policy_code}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Ex: 301826051"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Numéro de réservation</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="reservation_number"
                                            value={filters.reservation_number}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Numéro de réservation"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Prénom client</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="client_first_name"
                                            value={filters.client_first_name}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Prénom du client"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Nom client</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="client_last_name"
                                            value={filters.client_last_name}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Nom du client"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Email client</Form.Label>
                                        <Form.Control
                                            type="email"
                                            name="client_email"
                                            value={filters.client_email}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Email du client"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date début activité</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="activity_start_date"
                                            value={filters.activity_start_date}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Jour du séjour (trip day)</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="trip_day"
                                            value={filters.trip_day}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <Button
                            variant="primary"
                            onClick={handleOpenEmailModal}
                            disabled={forecastsState.loading}
                            className="d-flex align-items-center"
                        >
                            <FiMail className="me-2" />
                            Envoyer les emails
                        </Button>
                        <Button
                            variant="outline-secondary"
                            onClick={handleRefresh}
                            disabled={forecastsState.loading}
                            className="d-flex align-items-center"
                        >
                            <FiRefreshCw className={`me-2 ${forecastsState.loading ? 'spinning' : ''}`} />
                            Actualiser
                        </Button>
                    </div>

                    {forecastsState.loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">Chargement des prévisions (trips)...</p>
                        </div>
                    ) : forecastsState.error ? (
                        <div className="text-center py-5">
                            <Alert variant="danger">{forecastsState.error}</Alert>
                        </div>
                    ) : currentForecasts.length === 0 ? (
                        <div className="text-center py-5">
                            <FiCloudRain size={48} className="text-muted mb-3" />
                            <p className="text-muted">Aucune prévision disponible</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <div className="table-scroll">
                                <Table hover striped className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>Code police</th>
                                            <th>Prénom</th>
                                            <th>Nom</th>
                                            <th>E-mail</th>
                                            <th>Jour du séjour</th>
                                            <th>Paramètre météo</th>
                                            <th>Partenaire</th>
                                            <th>Nom référence</th>
                                            <th>Début</th>
                                            <th>Fin</th>
                                            <th className="text-end col-actions">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Array.isArray(currentForecasts) && currentForecasts.length > 0 ? (
                                            currentForecasts.map((item, idx) => (
                                                <tr key={getDeep(item, ['forecast_info', 'forecast_id'], getDeep(item, ['policy_info', 'policy_id'], idx))}>
                                                    <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{getDeep(item, ['policy_info', 'policy_code'])}</td>
                                                    <td>{getDeep(item, ['client_info', 'first_name'])}</td>
                                                    <td>{getDeep(item, ['client_info', 'last_name'])}</td>
                                                    <td>{getDeep(item, ['client_info', 'email'])}</td>
                                                    <td>{getDeep(item, ['forecast_info', 'trip_day'])}</td>
                                                    <td>{formatWeatherParameter(item)}</td>
                                                    <td>{getDeep(item, ['partner_info', 'company_name'])}</td>
                                                    <td>{getDeep(item, ['reference_info', 'reference_name'])}</td>
                                                    <td>{getDeep(item, ['activity_info', 'activity_start_date'], getDeep(item, ['activity_info', 'trip_effective_start_date']))}</td>
                                                    <td>{getDeep(item, ['activity_info', 'activity_end_date'], getDeep(item, ['activity_info', 'trip_effective_end_date']))}</td>
                                                    <td className="text-end col-actions">
                                                        <Button variant="outline-info" size="sm" className="me-2" onClick={() => handleDetails(item)} title="Détails" aria-label="Détails">
                                                            <FiEye />
                                                        </Button>
                                                        <Button variant="outline-primary" size="sm" className="me-2" onClick={() => handleEdit(item)} title="Modifier" aria-label="Modifier">
                                                            <FiEdit />
                                                        </Button>
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            onClick={() => handleDelete(item)}
                                                            title="Supprimer"
                                                            aria-label="Supprimer"
                                                            disabled={deleteLoading}
                                                        >
                                                            <FiTrash2 />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="11" className="text-center">
                                                    Aucune prévision trouvée
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    )}

                    {/* Pagination */}
                    {totalItems > 0 && totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center mt-4">
                            <div className="text-muted">
                                Affichage de {indexOfFirstItem + 1} à {indexOfLastItem} sur {totalItems} prévision{totalItems > 1 ? 's' : ''}
                            </div>
                            <Pagination className="mb-0">
                                <Pagination.First
                                    onClick={() => {
                                        setCurrentPage(1);
                                        loadForecasts(1);
                                    }}
                                    disabled={currentPage === 1}
                                />
                                <Pagination.Prev
                                    onClick={() => {
                                        const prevPage = currentPage - 1;
                                        setCurrentPage(prevPage);
                                        loadForecasts(prevPage);
                                    }}
                                    disabled={currentPage === 1}
                                />
                                {(() => {
                                    const { startPage, endPage } = getPaginationRange();
                                    const pages = [];

                                    // Show first page if not in range
                                    if (startPage > 1) {
                                        pages.push(
                                            <Pagination.Item key={1} onClick={() => {
                                                setCurrentPage(1);
                                                loadForecasts(1);
                                            }}>
                                                1
                                            </Pagination.Item>
                                        );
                                        if (startPage > 2) {
                                            pages.push(<Pagination.Ellipsis key="ellipsis-start" />);
                                        }
                                    }

                                    // Show pages in range
                                    for (let i = startPage; i <= endPage; i++) {
                                        pages.push(
                                            <Pagination.Item
                                                key={i}
                                                active={i === currentPage}
                                                onClick={() => {
                                                    setCurrentPage(i);
                                                    loadForecasts(i);
                                                }}
                                            >
                                                {i}
                                            </Pagination.Item>
                                        );
                                    }

                                    // Show last page if not in range
                                    if (endPage < totalPages) {
                                        if (endPage < totalPages - 1) {
                                            pages.push(<Pagination.Ellipsis key="ellipsis-end" />);
                                        }
                                        pages.push(
                                            <Pagination.Item key={totalPages} onClick={() => {
                                                setCurrentPage(totalPages);
                                                loadForecasts(totalPages);
                                            }}>
                                                {totalPages}
                                            </Pagination.Item>
                                        );
                                    }

                                    return pages;
                                })()}
                                <Pagination.Next
                                    onClick={() => {
                                        const nextPage = currentPage + 1;
                                        setCurrentPage(nextPage);
                                        loadForecasts(nextPage);
                                    }}
                                    disabled={currentPage === totalPages}
                                />
                                <Pagination.Last
                                    onClick={() => {
                                        setCurrentPage(totalPages);
                                        loadForecasts(totalPages);
                                    }}
                                    disabled={currentPage === totalPages}
                                />
                            </Pagination>
                        </div>
                    )}

                    {/* Modal de confirmation pour l'envoi d'email */}
                    <Modal show={showEmailModal} onHide={handleCloseEmailModal} size="lg">
                        <Modal.Header closeButton>
                            <Modal.Title>
                                <FiMail className="me-2" />
                                Envoyer les emails de prévisions déclenchées
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {emailLoading ? (
                                <div className="text-center py-4">
                                    <Spinner animation="border" variant="primary" />
                                    <p className="mt-3">Envoi des emails en cours...</p>
                                </div>
                            ) : emailResult ? (
                                <Alert variant={emailResult.status === 'success' ? 'success' : 'warning'}>
                                    <Alert.Heading>
                                        {emailResult.status === 'success' ? 'Emails envoyés avec succès !' : 'Avertissement'}
                                    </Alert.Heading>
                                    <p><strong>Message :</strong> {emailResult.message || 'Opération terminée'}</p>
                                    {emailResult.count !== undefined && (
                                        <p><strong>Nombre d'emails envoyés :</strong> {emailResult.count}</p>
                                    )}
                                    {emailResult.timestamp && (
                                        <p className="text-muted mb-0">
                                            <small>Timestamp : {new Date(emailResult.timestamp).toLocaleString('fr-FR')}</small>
                                        </p>
                                    )}
                                </Alert>
                            ) : emailError ? (
                                <Alert variant="danger">
                                    <Alert.Heading>Erreur lors de l'envoi</Alert.Heading>
                                    <p>{emailError}</p>
                                </Alert>
                            ) : (
                                <>
                                    <p>
                                        Êtes-vous sûr de vouloir envoyer les emails de prévisions déclenchées aux clients?
                                    </p>
                                    <p className="text-muted">
                                        Cette action enverra un email avec les informations de prévisions déclenchées à tous les administrateurs concernés.
                                    </p>
                                </>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            {emailResult || emailError ? (
                                <Button variant="secondary" onClick={handleCloseEmailModal}>
                                    Fermer
                                </Button>
                            ) : (
                                <>
                                    <Button variant="secondary" onClick={handleCloseEmailModal} disabled={emailLoading}>
                                        Annuler
                                    </Button>
                                    <Button variant="primary" onClick={handleSendEmail} disabled={emailLoading}>
                                        {emailLoading ? (
                                            <>
                                                <Spinner size="sm" className="me-2" animation="border" />
                                                Envoi en cours...
                                            </>
                                        ) : (
                                            <>
                                                <FiMail className="me-2" />
                                                Confirmer l'envoi
                                            </>
                                        )}
                                    </Button>
                                </>
                            )}
                        </Modal.Footer>
                    </Modal>

                    {/* Modal de confirmation suppression forecast */}
                    <Modal show={showDeleteModal} onHide={handleCloseDeleteModal} centered>
                        <Modal.Header closeButton={!deleteLoading}>
                            <Modal.Title>
                                <FiTrash2 className="me-2" />
                                Confirmer la suppression
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {deleteError && (
                                <Alert variant="danger" className="mb-3">
                                    {deleteError}
                                </Alert>
                            )}
                            <p className="mb-2">
                                Voulez-vous vraiment supprimer cette prévision ?
                            </p>
                            <p className="mb-0 text-muted">
                                ID forecast: <code>{getDeep(selectedForecast, ['forecast_info', 'forecast_id'], 'N/A')}</code>
                            </p>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleCloseDeleteModal} disabled={deleteLoading}>
                                Annuler
                            </Button>
                            <Button variant="danger" onClick={confirmDelete} disabled={deleteLoading}>
                                {deleteLoading ? (
                                    <>
                                        <Spinner size="sm" className="me-2" animation="border" />
                                        Suppression...
                                    </>
                                ) : (
                                    'Supprimer'
                                )}
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminTripsForecasts;


